import type { ApiError } from '../api/apiError';

const STATUS_MESSAGES: Record<number, string> = {
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to do that.',
  404: 'The requested item could not be found.',
  409: 'This action conflicts with the current state. Please refresh and try again.',
  422: 'Some of the information provided is invalid.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on the server. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The server is temporarily unavailable. Please try again later.',
};

const CODE_MESSAGES: Record<string, string> = {
  NETWORK_ERROR: 'Unable to connect to the server. Check your connection and try again.',
  REQUEST_TIMEOUT: 'The request timed out. Please try again.',
  INVALID_SESSION: 'Your session is no longer available. Please sign in again.',
  ACCOUNT_NOT_AVAILABLE: 'This account is not available. Please sign in again.',
  ACCESS_DENIED: 'You do not have permission to do that.',
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait a moment and try again.',
  KIOSK_INACTIVE: 'This kiosk has been deactivated. Contact an administrator.',
  KIOSK_UNAUTHORIZED: 'This kiosk is no longer authorized. Activate it again.',
  KIOSK_DEVICE_MISMATCH:
    'This device does not match the registered kiosk. Activate again on the correct device.',
  KIOSK_NOT_ACTIVATED:
    'This kiosk is not activated. Scan the activation QR from the admin portal.',
  INVALID_ACTIVATION_TOKEN:
    'This activation code is invalid or has expired. Generate a new one in the admin portal.',
  INVALID_REFRESH_TOKEN: 'Your session is no longer available. Please sign in again.',
  TASK_LIMIT_REACHED: 'This column has reached its task limit.',
  TASK_MOVE_BLOCKED:
    'This task cannot be moved until Member, Due Date, and Time Estimate are completed.',
  TASK_DETAILS_REQUIRED: 'Complete the required task information for this column.',
  WFH_DEVICE_REQUIRED:
    'A device identifier is required for work-from-home check-in.',
  WFH_CHECK_IN_PENDING: 'WFH request is pending approval.',
  WFH_CHECK_IN_REJECTED: 'WFH request was rejected.',
  WFH_CHECK_IN_NOT_APPROVED:
    'WFH check-in requires an approved WFH request for this date.',
  UNSUPPORTED_ATTENDANCE_SOURCE:
    'This work location cannot be recorded yet. Ask an administrator to allow Client Office and Client/Prospect Visit attendance.',
  LEAVE_DURATION_REQUIRED: 'Enable full-day or half-day leave for this leave type.',
  LEAVE_CARRY_FORWARD_INVALID:
    'Carry forward settings are invalid. Turn carry forward on or off, and use a maximum between 0 and 365 days or leave it blank for no limit.',
  LEAVE_TYPE_RETIRED: 'This leave type has been retired and cannot be created again.',
  LEAVE_CONFIGURATION_LOAD_FAILED:
    'Leave configuration was saved but could not be loaded. Pull to refresh the list.',
};

const UNSAFE_PATTERNS = [
  /\bselect\b.+\bfrom\b/i,
  /\binsert\b.+\binto\b/i,
  /\bupdate\b.+\bset\b/i,
  /\bdelete\b.+\bfrom\b/i,
  /\bexec(ute)?\b/i,
  /\bsp_\w+/i,
  /\bdbo\./i,
  /stack\s*trace/i,
  /at\s+\S+\s*\(/i,
  /exception\s*:/i,
  /ora-\d+/i,
  /sqlstate/i,
  /postgres/i,
  /mongodb/i,
  /sequelize/i,
  /prisma/i,
  /nestjs/i,
  /internal\s+server\s+error/i,
  /check constraint/i,
  /\bconstraint\b/i,
  /primary key/i,
  /duplicate entry/i,
  /deadlock/i,
  /\bchk_\w+/i,
  /ECONNREFUSED/i,
  /ENOTFOUND/i,
  /ETIMEDOUT/i,
];

const GENERIC_SERVER_BLURBS = [
  'internal server error',
  'something went wrong',
  'something went wrong on the server',
  'service temporarily unavailable',
  'the server is temporarily unavailable',
];

function looksUnsafe(message: string): boolean {
  if (message.length > 280) return true;
  return UNSAFE_PATTERNS.some(pattern => pattern.test(message));
}

function isGenericServerBlurb(message: string): boolean {
  const normalized = message.trim().toLowerCase().replace(/[.!]+$/, '');
  return GENERIC_SERVER_BLURBS.some(blurb => normalized === blurb);
}

/**
 * Turns an ApiError into a short, user-safe message.
 * Never forwards SQL, stack traces, or procedure names.
 */
export function toFriendlyErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const apiError = error as ApiError | undefined;
  const code = apiError?.code;
  const raw = typeof apiError?.message === 'string' ? apiError.message.trim() : '';
  const safeRaw = raw && !looksUnsafe(raw) && !isGenericServerBlurb(raw) ? raw : '';
  if (safeRaw) return safeRaw;
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];

  const status = apiError?.statusCode;
  if (status != null && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status];
  }
  if (status != null && status >= 500) {
    return STATUS_MESSAGES[500];
  }

  if (!raw || looksUnsafe(raw)) return fallback;
  return raw;
}
